/**
 * Custody schedule between Lev and Nigar.
 *
 * Pattern: 3-2-2 alternating over 14 days. On the anchor week (starting
 * Sat 16 Aug 2026) the kids are with Nigar Sat–Mon (3), Lev Tue–Wed (2)
 * and Nigar Thu–Fri (2). The following week the roles flip: Lev Sat–Mon,
 * Nigar Tue–Wed, Lev Thu–Fri. Then it repeats.
 *
 * All logic is local-date only — no timezone math. That way "which parent
 * has the kids today" is decided by the wall calendar, not by UTC.
 */

const ANCHOR_YMD = '2026-08-16'; // Sat = cycle day 0

// 14-day pattern indexed by cycle day (0 = Sat of week A).
const PATTERN = [
  'nigar', 'nigar', 'nigar', 'me', 'me', 'nigar', 'nigar', // Sat–Fri week A
  'me', 'me', 'me', 'nigar', 'nigar', 'me', 'me',           // Sat–Fri week B
];

const localMidnight = (input) => {
  const d = typeof input === 'string' ? new Date(input) : new Date(input);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const anchorMs = localMidnight(`${ANCHOR_YMD}T00:00:00`);

const cycleDayFor = (input) => {
  const ms = localMidnight(input);
  const days = Math.round((ms - anchorMs) / 86400000);
  return ((days % 14) + 14) % 14;
};

/** 'me' | 'nigar' for the given local date. */
export const custodyFor = (input = new Date()) => PATTERN[cycleDayFor(input)];

/** True if today (or the given date) is a kids day for Lev. */
export const isKidsDay = (input = new Date()) => custodyFor(input) === 'me';

/** Returns { date, from, to, daysUntil } describing the next parent swap. */
export const nextTransition = (input = new Date()) => {
  const currentParent = custodyFor(input);
  for (let i = 1; i < 14; i++) {
    const d = new Date(input);
    d.setDate(d.getDate() + i);
    const p = custodyFor(d);
    if (p !== currentParent) {
      return { date: d, from: currentParent, to: p, daysUntil: i };
    }
  }
  return null;
};

/** 14 days starting from the Sunday of the week containing `input`. */
export const currentCycleDays = (input = new Date()) => {
  // Week starts on Sunday. getDay(): Sun=0 ... Sat=6.
  const start = new Date(input);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 14 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { date: d, parent: custodyFor(d) };
  });
};

export const PARENT_LABEL = { me: 'Lev', nigar: 'Nigar' };
